import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { API_ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/stores/useAuthStore'

export interface OrderItemDetail {
    id: number
    project: string | null
    project_name: string
    front_page: number | null
    back_page: number | null
    front_page_number: number | null
    back_page_number: number | null
    material: number
    material_name: string
    size: number
    size_name: string
    quantity_millares: number
    price_per_millar: string
    subtotal: string
    notes: string
    discount_applied: string
    tier_min_millares: number | null
}

export interface OrderListItem {
    id: number
    uuid: string
    order_number: string
    status: string
    payment_status: string
    total: string
    item_count: number
    first_project_name: string
    created: string
}

export interface OrderDetail {
    id: number
    uuid: string
    order_number: string
    status: string
    payment_status: string
    items: OrderItemDetail[]
    items_total: string
    shipping_cost: string
    total: string
    shipping_address: number
    billing_info: number | null
    tracking_number: string
    notes: string
    payment_method: string
    izipay_transaction_id: string
    status_logs: {
        id: number
        old_status: string
        new_status: string
        changed_by_email: string
        note: string
        created: string
    }[]
    payments: {
        id: number
        method: string
        amount: string
        proof_url: string
        status: string
        transaction_id: string
        created: string
    }[]
    created: string
    updated: string
}

export interface CreateOrderItemPayload {
    project: string
    front_page: number
    back_page?: number | null
    material: number
    size: number
    quantity_millares: number
    notes?: string
}

export interface CreateOrderPayload {
    items: CreateOrderItemPayload[]
    shipping_address: number
    billing_info?: number | null
    notes?: string
}

interface PaginatedResponse<T> {
    count: number
    next: string | null
    previous: string | null
    results: T[]
}

const KEYS = {
    all: ['orders'] as const,
    list: () => [...KEYS.all, 'list'] as const,
    detail: (uuid: string) => [...KEYS.all, 'detail', uuid] as const,
}

export function useOrders() {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<PaginatedResponse<OrderListItem>>(API_ROUTES.orders)
            return data.results
        },
        enabled: isLogged,
        refetchInterval: 30_000,
    })
}

export function useOrder(uuid: string) {
    const { isLogged } = useAuthStore()
    return useQuery({
        queryKey: KEYS.detail(uuid),
        queryFn: async () => {
            const { data } = await api.get<OrderDetail>(`${API_ROUTES.orders}${uuid}/`)
            return data
        },
        enabled: isLogged && !!uuid,
        refetchInterval: 15_000,
    })
}

export function useCreateOrder() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: CreateOrderPayload) => {
            const { data } = await api.post<OrderDetail>(API_ROUTES.orders, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}

export function useCancelOrder() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (uuid: string) => {
            const { data } = await api.patch<OrderDetail>(`${API_ROUTES.orders}${uuid}/cancel/`)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}

export function useOrderKeys() {
    return KEYS
}
